import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { ArrowLeft, UserPlus, Trash2, Edit2, Power, Loader2, Mail, Clock, Shield, X, AlertTriangle, Archive, Download, Database, RotateCcw, Filter } from "lucide-react";
import { toast } from "sonner";
import EkinBrand from "@/components/EkinBrand";

const ROLES = [
  { id: "master", name: "Master (super admin)" },
  { id: "administrador", name: "Administrador" },
  { id: "ventas", name: "Ventas" },
  { id: "contador", name: "Contador" },
  { id: "logistica", name: "Logística" },
  { id: "chofer", name: "Chofer" },
  { id: "entregas_locales", name: "Entregas locales" },
  { id: "consulta", name: "Consulta (solo lectura)" },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.id, r.name]));

function formatErr(detail) {
  if (!detail) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return detail?.msg || String(detail);
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("ventas");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || !name.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/users`, {
        email: email.trim().toLowerCase(),
        password, name: name.trim(), role,
      });
      toast.success("Usuario creado");
      onCreated(res.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-zinc-950/70 backdrop-blur-sm p-4" data-testid="modal-create-user">
      <div className="bg-white border border-zinc-200 w-full max-w-md my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div>
            <p className="eyebrow text-zinc-500">Nuevo</p>
            <h2 className="font-display text-xl font-bold mt-1">Crear usuario</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100" data-testid="btn-close-create"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Nombre</label>
            <input data-testid="create-input-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Email</label>
            <input data-testid="create-input-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Contraseña (mín 8)</label>
            <input data-testid="create-input-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Rol</label>
            <select data-testid="create-input-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 bg-white">
              {ROLES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-tactile flex-1 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50">Cancelar</button>
            <button type="submit" disabled={submitting} data-testid="btn-submit-create" className="btn-tactile flex-[2] py-3 bg-ekin-600 text-white text-sm font-medium hover:bg-ekin-700 disabled:opacity-50">
              {submitting ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onUpdated }) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { name: name.trim(), role };
      if (password) body.password = password;
      const res = await axios.patch(`${API}/admin/users/${user.user_id}`, body);
      toast.success("Usuario actualizado");
      onUpdated(res.data);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-zinc-950/70 backdrop-blur-sm p-4" data-testid="modal-edit-user">
      <div className="bg-white border border-zinc-200 w-full max-w-md my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div className="min-w-0 flex-1 pr-3">
            <p className="eyebrow text-zinc-500">Editar</p>
            <h2 className="font-display text-xl font-bold mt-1 truncate">{user.email}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 flex-shrink-0" data-testid="btn-close-edit"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Nombre</label>
            <input data-testid="edit-input-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Rol</label>
            <select data-testid="edit-input-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950 bg-white">
              {ROLES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Nueva contraseña (opcional)</label>
            <input data-testid="edit-input-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Dejar en blanco para no cambiar" className="w-full px-4 py-3 border border-zinc-300 text-sm focus:outline-none focus:border-zinc-950" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-tactile flex-1 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50">Cancelar</button>
            <button type="submit" disabled={submitting} data-testid="btn-submit-edit" className="btn-tactile flex-[2] py-3 bg-ekin-600 text-white text-sm font-medium hover:bg-ekin-700 disabled:opacity-50">
              {submitting ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ResetMasterPasswordCard() {
  const [users, setUsers] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/users`);
        setUsers((res.data || []).filter((u) => u.role === "master" && u.is_active));
      } catch (err) {
        console.error("[Admin] load masters failed:", err);
      }
    })();
  }, []);

  const validate = () => {
    if (!targetId) return "Selecciona un usuario master";
    if (newPass.length < 10) return "La contraseña debe tener ≥ 10 caracteres";
    if (!/\d/.test(newPass)) return "Debe contener al menos un número";
    if (!/[A-Z]/.test(newPass)) return "Debe contener al menos una mayúscula";
    if (newPass !== confirmPass) return "Las contraseñas no coinciden";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    const target = users.find((u) => u.user_id === targetId);
    if (!window.confirm(
      `Vas a restablecer la contraseña de ${target?.email}. La sesión actual de ese usuario seguirá activa hasta que cierre y vuelva a entrar.\n\n¿Continuar?`,
    )) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/users/${targetId}`, { password: newPass });
      toast.success(`Contraseña de ${target?.email} restablecida`);
      setNewPass(""); setConfirmPass(""); setTargetId("");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (users.length === 0) return null;

  return (
    <div className="border-2 border-indigo-300 bg-indigo-50 p-5" data-testid="reset-master-card">
      <div className="flex items-start gap-3 mb-4">
        <Shield className="h-6 w-6 text-indigo-700 flex-shrink-0 mt-1" />
        <div>
          <p className="font-display text-xl font-bold text-indigo-900">Reset rápido de contraseña master</p>
          <p className="text-sm text-indigo-800 mt-1">
            Restablece la contraseña de otro usuario master sin pasar por el modal de edición.
            Útil si un master colega olvida su contraseña.
          </p>
          <p className="text-xs text-indigo-700 mt-1.5">
            <strong>Tip:</strong> Si TÚ eres el único master y quedas bloqueado, usa el script de emergencia
            <code className="mx-1 px-1.5 py-0.5 bg-white border border-indigo-300">
              python /app/backend/reset_master.py &lt;email&gt; &lt;nueva-pass&gt;
            </code>
            desde la terminal del contenedor.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="eyebrow text-indigo-900 mb-1.5 block">Usuario master</label>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            data-testid="reset-master-target"
            className="w-full px-3 py-2.5 border-2 border-indigo-300 bg-white text-sm focus:outline-none focus:border-indigo-600"
          >
            <option value="">— Seleccionar —</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="eyebrow text-indigo-900 mb-1.5 block">Nueva contraseña</label>
          <input
            type="password" value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            placeholder="≥10, mayúscula y número"
            data-testid="reset-master-pass"
            className="w-full px-3 py-2.5 border-2 border-indigo-300 bg-white text-sm focus:outline-none focus:border-indigo-600"
          />
        </div>
        <div>
          <label className="eyebrow text-indigo-900 mb-1.5 block">Confirmar</label>
          <input
            type="password" value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            data-testid="reset-master-pass-confirm"
            className="w-full px-3 py-2.5 border-2 border-indigo-300 bg-white text-sm focus:outline-none focus:border-indigo-600"
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={submitting || !targetId || !newPass || !confirmPass}
        data-testid="btn-reset-master-submit"
        className="btn-tactile mt-4 px-5 py-3 bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-40 inline-flex items-center gap-2"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
        {submitting ? "Restableciendo..." : "Restablecer contraseña"}
      </button>
    </div>
  );
}

function PurgeByRangeCard() {
  const [field, setField] = useState("fecha_entrega");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [purging, setPurging] = useState(false);

  const FIELD_LABELS = {
    fecha_entrega: "Fecha pactada (fecha_entrega)",
    entregado_at: "Fecha real de entrega (entregado_at)",
    created_at: "Fecha de captura (created_at)",
  };

  const handlePreview = async () => {
    if (!dateFrom && !dateTo) {
      toast.error("Indica al menos una fecha (desde o hasta)");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await axios.post(`${API}/admin/purge/preview`, {
        field, date_from: dateFrom || null, date_to: dateTo || null,
      });
      setPreview(res.data);
      if (res.data.count === 0) toast.info("Ninguna entrega coincide con esos filtros");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const handlePurge = async () => {
    if (!preview || preview.count === 0) return;
    const text = window.prompt(
      `Esto eliminará ${preview.count} entrega(s) y sus ${preview.with_evidence} foto(s) de evidencia.\n\nSe creará un respaldo de seguridad automático antes.\n\nEscribe ELIMINAR para confirmar:`,
      "",
    );
    if (text !== "ELIMINAR") {
      if (text !== null) toast.error('Debes escribir "ELIMINAR" exacto');
      return;
    }
    setPurging(true);
    try {
      const res = await axios.post(`${API}/admin/purge/execute`, {
        field, date_from: dateFrom || null, date_to: dateTo || null,
        confirm: "ELIMINAR", auto_snapshot: true,
      });
      const safetyId = res.data.safety_backup_id;
      const deleted = res.data.deleted_deliveries;

      if (safetyId) {
        // Persistent toast with Undo action — 30s window
        toast(`${deleted} entrega${deleted === 1 ? "" : "s"} eliminada${deleted === 1 ? "" : "s"}`, {
          description: `Tienes 30 s para deshacer · respaldo ${safetyId}`,
          duration: 30000,
          action: {
            label: "Deshacer",
            onClick: async () => {
              const undoToastId = toast.loading("Restaurando…");
              try {
                await axios.post(`${API}/admin/backups/${safetyId}/restore`, {
                  confirm: "RESTORE", auto_snapshot: false,
                });
                toast.success(`Restaurado · ${deleted} entrega${deleted === 1 ? "" : "s"} de vuelta`, { id: undoToastId });
                window.dispatchEvent(new CustomEvent("ekin:backups-changed"));
                setTimeout(() => window.location.reload(), 1200);
              } catch (err) {
                console.error("[Admin] undo restore failed:", err);
                toast.error(formatErr(err?.response?.data?.detail) || "No se pudo deshacer", { id: undoToastId });
              }
            },
          },
        });
      } else {
        toast.success(`${deleted} entregas eliminadas`);
      }

      setPreview(null);
      setDateFrom(""); setDateTo("");
      // Refresh backups list above
      window.dispatchEvent(new CustomEvent("ekin:backups-changed"));
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="border-2 border-amber-300 bg-amber-50 p-5" data-testid="purge-card">
      <div className="flex items-start gap-3 mb-4">
        <Filter className="h-6 w-6 text-amber-700 flex-shrink-0 mt-1" />
        <div>
          <p className="font-display text-xl font-bold text-amber-900">Depurar entregas por rango</p>
          <p className="text-sm text-amber-800 mt-1">
            Elimina entregas que caigan en un rango de fechas. Se crea automáticamente un respaldo de seguridad antes,
            y se borran también las fotos de evidencia del object storage.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="eyebrow text-amber-900 mb-1.5 block">Campo</label>
          <select
            value={field}
            onChange={(e) => { setField(e.target.value); setPreview(null); }}
            data-testid="purge-field"
            className="w-full px-3 py-2.5 border-2 border-amber-300 bg-white text-sm focus:outline-none focus:border-amber-600"
          >
            {Object.entries(FIELD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow text-amber-900 mb-1.5 block">Desde</label>
          <input
            type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPreview(null); }}
            data-testid="purge-from"
            className="w-full px-3 py-2.5 border-2 border-amber-300 bg-white text-sm focus:outline-none focus:border-amber-600"
          />
        </div>
        <div>
          <label className="eyebrow text-amber-900 mb-1.5 block">Hasta</label>
          <input
            type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPreview(null); }}
            data-testid="purge-to"
            className="w-full px-3 py-2.5 border-2 border-amber-300 bg-white text-sm focus:outline-none focus:border-amber-600"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handlePreview}
          disabled={previewing || (!dateFrom && !dateTo)}
          data-testid="btn-purge-preview"
          className="btn-tactile px-5 py-3 border-2 border-amber-600 text-amber-900 bg-white text-sm font-medium hover:bg-amber-100 disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
          Vista previa
        </button>
        <button
          onClick={handlePurge}
          disabled={!preview || preview.count === 0 || purging}
          data-testid="btn-purge-execute"
          className="btn-tactile px-5 py-3 bg-amber-700 text-white text-sm font-medium hover:bg-amber-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          {preview && preview.count > 0 ? `Eliminar ${preview.count} entrega${preview.count === 1 ? "" : "s"}` : "Eliminar"}
        </button>
      </div>

      {preview && (
        <div className="mt-4 bg-white border border-amber-200 p-3 text-xs" data-testid="purge-preview-result">
          <p className="font-medium text-amber-900">
            {preview.count} entrega{preview.count === 1 ? "" : "s"} coinciden ·
            {" "}{preview.with_evidence} con foto de evidencia
          </p>
          {preview.count > 0 && Object.keys(preview.by_stage).length > 0 && (
            <p className="mt-1 text-amber-800">
              Por etapa: {Object.entries(preview.by_stage).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          )}
          {preview.preview && preview.preview.length > 0 && (
            <ul className="mt-2 max-h-32 overflow-y-auto text-amber-900 space-y-0.5">
              {preview.preview.slice(0, 10).map((d) => (
                <li key={d.id} className="font-mono text-[10px]">
                  · {d.id.slice(-8)} · {d.cliente} · {d.stage} · {d[field] || "—"}
                </li>
              ))}
              {preview.preview.length > 10 && (
                <li className="text-amber-700 italic">…y {preview.preview.length - 10} más</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function DangerZone() {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const loadBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backups`);
      setBackups(res.data);
    } catch (err) {
      console.error("[Admin] loadBackups failed:", err);
    }
  };
  useEffect(() => { loadBackups(); }, []);
  useEffect(() => {
    const onChanged = () => loadBackups();
    window.addEventListener("ekin:backups-changed", onChanged);
    return () => window.removeEventListener("ekin:backups-changed", onChanged);
  }, []);

  const handleReset = async () => {
    if (confirmText !== "RESET") {
      toast.error('Debes escribir "RESET" para confirmar');
      return;
    }
    if (!window.confirm("Esto borrará entregas, archivos, historial de accesos, intentos de login y sesiones. ¿Continuar?")) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/reset-operativo`, { confirm: "RESET" });
      setLastResult(res.data);
      setConfirmText("");
      toast.success("Reset operativo completado");
      loadBackups();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/backups/create`, { note });
      toast.success(`Respaldo creado · ${res.data.id}`);
      setNote("");
      loadBackups();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (b) => {
    setDownloading(b.id);
    try {
      const res = await axios.get(`${API}/admin/backups/${b.id}/download`, { responseType: "blob" });
      const day = (b.created_at || "").slice(0, 10);
      downloadBlob(res.data, `backup_${day}_${b.id}.zip`);
      toast.success("Respaldo descargado");
    } catch {
      toast.error("No se pudo descargar el respaldo");
    } finally {
      setDownloading(null);
    }
  };

  const handleRestore = async (b) => {
    const text = window.prompt(
      `Restaurar ${b.id}. Esto REEMPLAZARÁ todos los datos actuales con los del respaldo.\n\nSe creará un respaldo de seguridad automático antes de restaurar.\n\nEscribe RESTORE para confirmar:`,
      "",
    );
    if (text !== "RESTORE") {
      if (text !== null) toast.error('Debes escribir "RESTORE" exacto');
      return;
    }
    setRestoringId(b.id);
    try {
      const res = await axios.post(`${API}/admin/backups/${b.id}/restore`, {
        confirm: "RESTORE", auto_snapshot: true,
      });
      toast.success(`Restaurado desde ${b.id}`);
      if (res.data?.safety_backup_id) {
        toast.info(`Seguridad guardada: ${res.data.safety_backup_id}`);
      }
      loadBackups();
      // Reload page after a moment so all state refreshes (the current session is still valid).
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setRestoringId(null);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`¿Eliminar el respaldo ${b.id}? Esta acción es irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/backups/${b.id}`);
      toast.success("Respaldo eliminado");
      loadBackups();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="danger-zone">
      {/* Manual full backup */}
      <div className="border-2 border-emerald-300 bg-emerald-50 p-5" data-testid="manual-backup-card">
        <div className="flex items-start gap-3 mb-4">
          <Database className="h-6 w-6 text-emerald-700 flex-shrink-0 mt-1" />
          <div>
            <p className="font-display text-xl font-bold text-emerald-900">Respaldo completo manual</p>
            <p className="text-sm text-emerald-800 mt-1">
              Genera un snapshot de <strong>todas las colecciones</strong> (usuarios, entregas, archivos, historial, etc.).
              El archivo ZIP descargable incluye <code>database.json</code> y la carpeta <code>evidencias/</code> con todas las fotos.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota opcional (ej: 'Cierre de mes mayo')"
            data-testid="input-backup-note"
            className="flex-1 px-4 py-3 border-2 border-emerald-300 bg-white text-sm focus:outline-none focus:border-emerald-600"
          />
          <button
            onClick={handleCreateBackup}
            disabled={creating}
            data-testid="btn-create-backup"
            className="btn-tactile px-5 py-3 bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
            {creating ? "Generando..." : "Crear respaldo ahora"}
          </button>
        </div>
      </div>

      {/* Reset password de master */}
      <ResetMasterPasswordCard />

      {/* Purge by date range */}
      <PurgeByRangeCard />

      {/* Reset operativo */}
      <div className="border-2 border-rose-300 bg-rose-50 p-5">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-rose-600 flex-shrink-0 mt-1" />
          <div>
            <p className="font-display text-xl font-bold text-rose-900">Reset operativo</p>
            <p className="text-sm text-rose-800 mt-1">
              Borra <strong>entregas, archivos de evidencia, historial de accesos, intentos de login y sesiones activas</strong>.
              Los usuarios se conservan. Antes de borrar, se guarda un respaldo automático en la base de datos
              (visible abajo).
            </p>
          </div>
        </div>
        <label className="block text-xs eyebrow text-rose-800 mb-2">
          Escribe <code className="bg-white px-1.5 py-0.5 border border-rose-200 text-rose-900">RESET</code> para confirmar
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET"
            data-testid="input-reset-confirm"
            className="flex-1 px-4 py-3 border-2 border-rose-300 bg-white text-sm focus:outline-none focus:border-rose-600"
          />
          <button
            onClick={handleReset}
            disabled={confirmText !== "RESET" || submitting}
            data-testid="btn-reset-operativo"
            className="btn-tactile px-5 py-3 bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Ejecutando..." : "Ejecutar reset"}
          </button>
        </div>
        {lastResult && (
          <div className="mt-4 bg-white border border-rose-200 p-3 text-xs" data-testid="reset-result">
            <p className="font-medium text-rose-900">Último reset OK · backup_id <code>{lastResult.backup_id}</code></p>
            <ul className="mt-2 text-rose-800 space-y-0.5">
              {Object.entries(lastResult.deleted).map(([k, v]) => (
                <li key={k}>· {k}: {v} eliminados</li>
              ))}
              <li>· usuarios conservados: {lastResult.users_preserved}</li>
            </ul>
          </div>
        )}
      </div>

      {/* Backups list */}
      <div className="bg-white border border-zinc-200" data-testid="backups-list">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-zinc-500" />
            <p className="eyebrow text-zinc-500">Respaldos guardados ({backups.length})</p>
          </div>
          <button
            onClick={loadBackups}
            data-testid="btn-refresh-backups"
            className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2"
          >
            Refrescar
          </button>
        </div>
        {backups.length === 0 ? (
          <p className="p-5 text-sm text-zinc-500">No hay respaldos todavía.</p>
        ) : (
          <ul>
            {backups.map((b) => (
              <li key={b.id} className="p-4 border-b border-zinc-100 last:border-0" data-testid={`backup-${b.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
                      <span className="font-mono">{b.id}</span>
                      <span className={`text-[10px] px-2 py-0.5 ${b.kind === "manual" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                        {b.kind === "manual" ? "Manual" : "Auto-reset"}
                      </span>
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {b.created_by_email} · {fmtDate(b.created_at)}
                    </p>
                    {b.note && <p className="text-xs text-zinc-700 mt-1 italic">&ldquo;{b.note}&rdquo;</p>}
                    {b.counts && (
                      <p className="text-[11px] text-zinc-500 mt-1.5">
                        {Object.entries(b.counts).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleDownload(b)}
                      disabled={downloading === b.id}
                      data-testid={`btn-download-backup-${b.id}`}
                      title="Descargar ZIP"
                      className="btn-tactile p-2 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40"
                    >
                      {downloading === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleRestore(b)}
                      disabled={restoringId === b.id}
                      data-testid={`btn-restore-backup-${b.id}`}
                      title="Restaurar"
                      className="btn-tactile p-2 border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                    >
                      {restoringId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(b)}
                      data-testid={`btn-delete-backup-${b.id}`}
                      title="Eliminar"
                      className="btn-tactile p-2 border border-rose-300 text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}



export default function Admin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("users");

  const fetchAll = useCallback(async () => {
    try {
      const [u, h] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/login-history?limit=200`),
      ]);
      setUsers(u.data);
      setHistory(h.data);
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`);
      toast.success("Usuario eliminado");
      fetchAll();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.patch(`${API}/admin/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Desactivado" : "Activado");
      fetchAll();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="p-2 hover:bg-zinc-100" data-testid="btn-back-dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <EkinBrand subtitle="Administración" />
          </div>
          <button onClick={() => setShowCreate(true)} data-testid="btn-new-user" className="btn-tactile inline-flex items-center gap-2 bg-ekin-600 text-white px-4 py-2 text-sm font-medium hover:bg-ekin-700">
            <UserPlus className="h-4 w-4" /> Nuevo usuario
          </button>
        </div>
      </nav>

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
        <p className="eyebrow text-zinc-500 mb-2">Panel master</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-8">Equipos y accesos.</h1>

        <div className="flex border-b border-zinc-200 mb-6">
          <button onClick={() => setTab("users")} data-testid="tab-users" className={`px-5 py-3 text-sm font-medium border-b-2 ${tab === "users" ? "border-ekin-600 text-ekin-700" : "border-transparent text-zinc-500"}`}>
            Usuarios ({users.length})
          </button>
          <button onClick={() => setTab("history")} data-testid="tab-history" className={`px-5 py-3 text-sm font-medium border-b-2 ${tab === "history" ? "border-ekin-600 text-ekin-700" : "border-transparent text-zinc-500"}`}>
            Historial de accesos
          </button>
          {me?.role === "master" && (
            <button onClick={() => setTab("danger")} data-testid="tab-danger" className={`px-5 py-3 text-sm font-medium border-b-2 ${tab === "danger" ? "border-rose-500 text-rose-700" : "border-transparent text-zinc-500"}`}>
              Zona peligrosa
            </button>
          )}
        </div>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 animate-spin inline" /></div>
        ) : tab === "users" ? (
          <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="users-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-4 eyebrow text-zinc-500">Nombre</th>
                  <th className="text-left p-4 eyebrow text-zinc-500">Email</th>
                  <th className="text-left p-4 eyebrow text-zinc-500">Rol</th>
                  <th className="text-left p-4 eyebrow text-zinc-500">Estado</th>
                  <th className="text-right p-4 eyebrow text-zinc-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                    <td className="p-4 font-medium">
                      {u.name}
                      {u.user_id === me?.user_id && <span className="ml-2 eyebrow text-emerald-600 text-[10px]">Tú</span>}
                    </td>
                    <td className="p-4 text-zinc-600">{u.email}</td>
                    <td className="p-4">
                      <span className="inline-block px-2 py-1 bg-zinc-100 text-zinc-800 text-xs font-medium">{ROLE_LABEL[u.role] || u.role}</span>
                    </td>
                    <td className="p-4">
                      {u.is_active ? (
                        <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                          <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full" /> Activo
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
                          <span className="h-1.5 w-1.5 bg-zinc-400 rounded-full" /> Inactivo
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setEditing(u)} data-testid={`btn-edit-${u.user_id}`} className="p-2 hover:bg-zinc-100" title="Editar"><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleToggleActive(u)} data-testid={`btn-toggle-${u.user_id}`} className="p-2 hover:bg-zinc-100" title={u.is_active ? "Desactivar" : "Activar"}><Power className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(u)} data-testid={`btn-delete-${u.user_id}`} className="p-2 hover:bg-rose-50 text-rose-600" title="Eliminar"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "history" ? (
          <div className="bg-white border border-zinc-200" data-testid="history-table">
            {history.length === 0 ? (
              <p className="p-8 text-center text-sm text-zinc-500">Sin registros de acceso.</p>
            ) : (
              <ul>
                {history.map((h) => (
                  <li key={`${h.user_id}-${h.at}-${h.ip}`} className="p-4 border-b border-zinc-100 last:border-0 flex items-center gap-4">
                    <Clock className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-zinc-400" /> {h.email}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">IP {h.ip} · {h.user_agent?.slice(0, 60) || "—"}</p>
                    </div>
                    <span className="text-xs text-zinc-500 flex-shrink-0">{fmtDate(h.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <DangerZone />
        )}
      </main>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onUpdated={() => { setEditing(null); fetchAll(); }} />}
    </div>
  );
}
